import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Cycle } from '../../models/data/cycle.model';

@Injectable({
  providedIn: 'root',
})
export class CycleService {
  private cachedCycle: Cycle | null = null;
  private cachedBanner: string = '';

  constructor(private http: HttpClient) {}

  /**
   * Gets the formatted cycle banner string
   * Caches the result for the session duration
   */
  getCycleBanner(): Observable<string> {
    // Return cached value if available
    if (this.cachedBanner) {
      return of(this.cachedBanner);
    }

    // Fetch from API if not cached
    // Note: Response may be double-serialized JSON string per documentation
    console.log('🔄 CYCLE SERVICE: Fetching cycle from:', `${environment.apiUrl}/cycle/current`);
    return this.http.get<any>(`${environment.apiUrl}/cycle/current`).pipe(
      map((response) => {
        console.log('🔄 CYCLE SERVICE: Raw response:', response, 'Type:', typeof response);
        
        // Handle double-serialized JSON string (per documentation)
        // HttpClient typically auto-parses JSON, but API may return string
        let cycleData: any;
        try {
          // If response is a string, parse it
          if (typeof response === 'string') {
            const firstParse = JSON.parse(response);
            console.log('🔄 CYCLE SERVICE: First parse result:', firstParse);
            // If first parse is still a string, parse again (double-serialized)
            cycleData = typeof firstParse === 'string' ? JSON.parse(firstParse) : firstParse;
          } else if (response && typeof response === 'object') {
            // If response is already an object (HttpClient auto-parsed), use it directly
            cycleData = response;
          } else {
            console.warn('🔄 CYCLE SERVICE: Unexpected response type:', typeof response);
            cycleData = response;
          }
        } catch (e) {
          console.error('🔄 CYCLE SERVICE: Error parsing cycle response:', e, response);
          // If parsing fails, try using response directly
          cycleData = response;
        }

        console.log('🔄 CYCLE SERVICE: Parsed cycle data:', cycleData);

        // API returns PascalCase properties, normalize them
        const cycleID = cycleData.CycleID || cycleData.cycleID;
        const cycleCode = cycleData.Cycle || cycleData.cycle;
        const name = cycleData.Name || cycleData.name;
        const startDate = cycleData.StartDate || cycleData.startDate;
        const endDate = cycleData.EndDate || cycleData.endDate;
        const fPYear = cycleData.FPYear || cycleData.fPYear;
        const fPYearOrder = cycleData.FPYearOrder || cycleData.fPYearOrder;
        const longName = cycleData.LongName || cycleData.longName;

        // Validate required fields
        if (!cycleData || (!startDate && !endDate && !name)) {
          console.warn('🔄 CYCLE SERVICE: Invalid cycle data:', cycleData);
          return '';
        }

        // Create Cycle model
        const cycleModel = new Cycle(
          cycleID,
          cycleCode,
          name,
          startDate,
          endDate,
          fPYear,
          fPYearOrder,
          longName,
        );

        // Cache the cycle
        this.cachedCycle = cycleModel;

        // Format the banner string
        const banner = this.formatCycleBanner(cycleModel);
        console.log('🔄 CYCLE SERVICE: Formatted banner:', banner);

        // Cache the banner
        this.cachedBanner = banner;

        return banner;
      }),
      catchError((error) => {
        console.error('🔄 CYCLE SERVICE: Error fetching cycle data:', error);
        // Return empty string on error
        return of('');
      }),
    );
  }

  /**
   * Gets the cycle banner string, preferring longName from the cycle model
   */
  private formatCycleBanner(cycle: Cycle): string {
    // Prefer longName if available
    if (cycle.longName) {
      return cycle.longName;
    }

    // Fallback to formatted dates if longName not available
    if (cycle.startDate && cycle.endDate) {
      try {
        const startDate = new Date(cycle.startDate);
        const endDate = new Date(cycle.endDate);

        const startFormatted = this.formatDate(startDate);
        const endFormatted = this.formatDate(endDate);

        return `${startFormatted} - ${endFormatted}`;
      } catch (error) {
        console.error('Error formatting cycle dates:', error);
        return cycle.name || '';
      }
    }

    // Final fallback to name
    return cycle.name || '';
  }

  /**
   * Formats a date to "Month Day, Year" format (e.g., "June 1, 2023")
   */
  private formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Gets the raw cycle data (cached if available)
   */
  getCycle(): Observable<Cycle | null> {
    if (this.cachedCycle) {
      return of(this.cachedCycle);
    }

    return this.getCycleBanner().pipe(
      map(() => this.cachedCycle),
    );
  }
}

